import { TENANT_PATHS, soxProcessFlowchartPath, soxProcessesListPath } from '../../routing/paths.ts';
import type { HygieneArea, HygieneFinding, HygieneSeverity, HygieneState } from '../../request/hygiene/hygieneRequests.ts';

export const AREA_LABELS: Record<HygieneArea, string> = {
  planning: 'Planning',
  process: 'Process',
  rcm: 'RCM',
  itgc: 'ITGC',
  control: 'Control',
  testing: 'Testing',
  issues: 'Issues',
  evidence: 'Evidence',
  data: 'Data',
};

export const AREA_ORDER: HygieneArea[] = ['planning', 'process', 'rcm', 'itgc', 'control', 'testing', 'issues', 'evidence', 'data'];

export const SEVERITY_LABELS: Record<HygieneSeverity, string> = { high: 'High', medium: 'Medium', low: 'Low' };

export const SEVERITY_HINT: Record<HygieneSeverity, string> = {
  high: 'audit impact',
  medium: 'weakens evidence',
  low: 'tidiness',
};

export const SEVERITY_PILL: Record<HygieneSeverity, string> = {
  high: 'bg-red-50 text-red-800 ring-red-200',
  medium: 'bg-amber-50 text-amber-800 ring-amber-200',
  low: 'bg-slate-100 text-slate-700 ring-slate-200',
};

export const STATE_LABELS: Record<HygieneState, string> = {
  open: 'Open',
  assigned: 'Assigned',
  pending_approval: 'Waiting for approval',
  accepted: 'Accepted',
  fixed: 'Fixed',
  suggested: 'AI review',
  rejected: 'Dismissed',
};

export const ACCEPT_REASONS: Array<{ value: string; label: string }> = [
  { value: 'not_applicable', label: 'Not applicable' },
  { value: 'accepted_risk', label: 'Accepted risk' },
  { value: 'fixed_elsewhere', label: 'Fixed elsewhere' },
  { value: 'false_positive', label: 'False positive' },
  { value: 'other', label: 'Other (explain in the note)' },
];

export function reasonLabel(code?: string): string {
  return ACCEPT_REASONS.find((r) => r.value === code)?.label ?? code ?? '';
}

export const EVENT_LABELS: Record<string, string> = {
  detected: 'Found by the check',
  fixed_by_check: 'Fixed (the check no longer finds it)',
  reopened_by_check: 'Reopened (found again)',
  acceptance_expired: 'Acceptance expired (the record changed)',
  assigned: 'Assigned',
  accept_requested: 'Acceptance requested',
  accepted: 'Accepted',
  approved: 'Acceptance approved',
  approval_rejected: 'Acceptance rejected',
  reopened: 'Reopened',
  ai_confirmed: 'AI suggestion confirmed',
  ai_dismissed: 'AI suggestion dismissed',
};

type CheckCopy = { why: string; fix: string };

/** Why each check matters to an auditor and how to fix it. Keyed by backend check code. */
export const CHECK_COPY: Record<string, CheckCopy> = {
  assessment_missing: {
    why: 'Scoping and materiality drive which processes and controls are in scope. Without a saved assessment, coverage cannot be shown.',
    fix: 'Open Risk Assessment and save the assessment for this audit year.',
  },
  assessment_wrong_year: {
    why: 'Scoping based on another year’s figures may miss accounts that became material this year.',
    fix: 'Replace the document with the filing-year statement, or re-run the assessment for this year.',
  },
  assessment_year_unreadable: {
    why: 'Nobody can confirm the assessment used this year’s figures.',
    fix: 'Check the document and its period in Risk Assessment and correct the period or replace the file.',
  },
  scoping_without_processes: {
    why: 'In-scope entities need documented processes and controls before testing can start.',
    fix: 'Document the processes for this audit year, or revisit the entity scoping conclusions.',
  },
  cycle_without_process: {
    why: 'An active cycle with no process has no documented flow, risks or controls.',
    fix: 'Add the cycle’s processes, or mark the cycle inactive if it is out of scope.',
  },
  process_without_current_flowchart: {
    why: 'Mappings are read from the published flowchart. A draft-only process has no mappings the rest of the app counts.',
    fix: 'Create the flowchart, or publish and activate the latest version.',
  },
  process_not_complete: {
    why: 'An incomplete process walkthrough weakens the design evidence for its controls.',
    fix: 'Finish the process documentation and mark it complete.',
  },
  risk_without_control: {
    why: 'A risk with no control is an unmitigated risk; the external auditor will ask what covers it.',
    fix: 'Map a control to the risk on its process flowchart, or retire the risk if it no longer applies.',
  },
  key_control_without_risk: {
    why: 'A key control must address a risk; otherwise it is unclear why it is relied on.',
    fix: 'Map the control to the risk it addresses, or reconsider whether it is key.',
  },
  risk_without_process: {
    why: 'A risk that is on no process is not part of the documented control environment for the year.',
    fix: 'Add the risk to the process where it arises, or retire it.',
  },
  high_risk_item_without_key_control: {
    why: 'A high-risk account needs key controls covering its relevant assertions. This check is approximate: assertions per account are not stored yet.',
    fix: 'Add or designate key controls with assertions for the cycle, or review the line item’s cycle mapping in Risk Assessment.',
  },
  itgc_risk_without_system: {
    why: 'ITGC risks are assessed per system. Without a system the risk is not covered.',
    fix: 'Link the risk to the in-scope systems on the Systems page.',
  },
  itgc_control_without_system: {
    why: 'Automated controls rely on ITGCs that are linked to the systems they run on.',
    fix: 'Link the control to its systems on the Systems page.',
  },
  system_missing_itgc_domain: {
    why: 'A key system needs ITGC coverage for access, change and operations, or automated controls on it cannot be relied on.',
    fix: 'Link ITGC controls for the missing domains to the system.',
  },
  control_draft: {
    why: 'A draft control is not final, so it cannot be tested or relied on.',
    fix: 'Complete the control and set it active.',
  },
  control_missing_fields: {
    why: 'Frequency, nature, assertions and owner drive sampling, testing and follow-up.',
    fix: 'Fill in the missing fields on the control.',
  },
  control_significance_missing: {
    why: 'Whether a control is key decides if it must be tested.',
    fix: 'Mark the control key or non-key.',
  },
  key_control_without_test_plan: {
    why: 'A key control must be tested; there is no plan saying how.',
    fix: 'Create and publish a test plan for the control.',
  },
  key_control_without_test_run: {
    why: 'The external auditor cannot rely on a key control that was not tested this year.',
    fix: 'Create a test run for this audit year from Control Testing.',
  },
  test_exception_without_issue: {
    why: 'A failed test or an exception must be evaluated as a deficiency and tracked to remediation.',
    fix: 'Raise an issue for the exception and link it, or mark the exception a false positive with a reason.',
  },
  issue_without_action_plan: {
    why: 'An open deficiency needs a remediation plan with an owner and a date.',
    fix: 'Add an action plan to the issue.',
  },
  action_plan_overdue: {
    why: 'Overdue remediation can turn a deficiency into a year-end finding.',
    fix: 'Complete the action plan or agree a new date with the owner.',
  },
  issue_unassigned: {
    why: 'Nobody owns the follow-up of this issue.',
    fix: 'Assign the issue to its owner.',
  },
  issue_unclassified: {
    why: 'Deficiencies must be classified to aggregate them for the year-end evaluation.',
    fix: 'Classify the issue as a control deficiency, significant deficiency or material weakness.',
  },
  pbc_overdue: {
    why: 'Missing client evidence delays testing.',
    fix: 'Follow up with the assignee, or update the request’s status.',
  },
  test_run_missing_evidence: {
    why: 'Test conclusions need evidence for every sample and step.',
    fix: 'Attach the missing evidence in the test run.',
  },
  duplicate_risk: {
    why: 'Duplicate risks split mappings and testing across two records.',
    fix: 'Merge the risks into one and remap their controls.',
  },
  duplicate_control: {
    why: 'Duplicate controls double the testing effort or leave one copy untested.',
    fix: 'Merge or retire one of the controls.',
  },
  ai_similar_risks: {
    why: 'The AI review found two risks that may describe the same thing.',
    fix: 'If they are duplicates, merge them; otherwise dismiss the suggestion.',
  },
  ai_similar_controls: {
    why: 'The AI review found two controls that may describe the same activity.',
    fix: 'If they are duplicates, merge or retire one; otherwise dismiss the suggestion.',
  },
  ai_mapping_mismatch: {
    why: 'The AI review thinks this control may not address the risk it is mapped to.',
    fix: 'Check the mapping and map a control that addresses the risk.',
  },
  ai_frequency_mismatch: {
    why: 'The control’s wording states a different frequency from its frequency field, which changes the sample size.',
    fix: 'Correct the frequency field or the wording.',
  },
};

export type FindingLink = { to: string; state?: Record<string, unknown>; label: string };

// Back arrows on the target pages return to Hygiene Check: controls read `returnPath`, cycles and processes
// read `processReturnPath`. Issue and system pages always go back to their own list.
const BACK = TENANT_PATHS.HYGIENE_CHECK;
const processBack = { processReturnPath: BACK };

/** Where "Open record" goes for a finding. */
export function findingLink(f: HygieneFinding): FindingLink | null {
  const code = f.record_code ?? '';
  switch (f.record_type) {
    case 'assessment':
    case 'line_item':
      return { to: TENANT_PATHS.MATERIALITY, label: 'Open Risk Assessment' };
    case 'cycle':
      return code ? { to: soxProcessesListPath(code), state: processBack, label: 'Open cycle' } : null;
    case 'process':
      return f.parent_code && code ? { to: soxProcessFlowchartPath(f.parent_code, code), state: processBack, label: 'Open process' } : { to: TENANT_PATHS.SOX_DASHBOARD, label: 'Open Process Charts' };
    case 'risk':
      return code ? { to: `/sox-risks/${encodeURIComponent(code)}`, label: 'Open risk' } : null;
    case 'control':
      return { to: `/sox-controls/view/${encodeURIComponent(code || f.record_id)}`, state: { returnPath: BACK }, label: 'Open control' };
    case 'test_run':
      return code
        ? { to: `/sox-controls/view/${encodeURIComponent(code)}`, state: { activeTab: 'test-runs', returnPath: BACK }, label: 'Open test runs' }
        : { to: TENANT_PATHS.CONTROL_TESTING_TEST_RUNS, label: 'Open test runs' };
    case 'system':
      return { to: `/sox-systems/${encodeURIComponent(f.record_id)}`, label: 'Open system' };
    case 'issue':
      return { to: `/issues/${encodeURIComponent(f.record_id)}`, label: 'Open issue' };
    case 'pbc':
      return { to: `${TENANT_PATHS.ANALYTICS_DASHBOARD}?widget=pbc_tracker`, label: 'Open PBC requests' };
    default:
      return null;
  }
}

export function formatDate(value?: string): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function ageLabel(since?: string, now: Date = new Date()): string {
  if (!since) return '';
  const days = Math.floor((now.getTime() - new Date(since).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return 'under a minute';
  const m = Math.round(seconds / 60);
  return m === 1 ? 'about a minute' : `about ${m} minutes`;
}
